import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { DataItemCellSelectGetDocument } from '~/components/inputs/DataItemCellSelect/DataItemCellSelect.generated';
import { useAppContext } from '~/contexts';
import { useDebounce } from '~/lib/debounce';
import type { StudioItem } from '../../context';

type Row = Partial<Record<string, string>>;
type RowsBySource = Partial<Record<number, Row>>;

const sourceIdsIn = (items: StudioItem[]) => [
  ...new Set(
    items.flatMap((item) =>
      item.type === 'text' && item.__deviceDataSourceId ? [item.__deviceDataSourceId] : [],
    ),
  ),
];

/**
 * The one lookup of the previewed device's rows. Everything that shows a previewed value, the
 * canvas and the Data tab caption, reads from here so they can never disagree. Applied at
 * render time only: nothing here reaches studio state, so a preview can never be saved.
 */
export const useDevicePreview = (items: StudioItem[], previewDeviceId: string) => {
  const client = useApolloClient();
  const { currentNetwork } = useAppContext();
  const [rows, setRows] = useState<RowsBySource>({});

  // Typed one character at a time, so wait for a pause rather than querying every keystroke.
  const settledDeviceId = useDebounce(previewDeviceId.trim(), 300);

  const sourceIds = sourceIdsIn(items);
  const sourceKey = sourceIds.join(',');

  useEffect(() => {
    const deviceId = settledDeviceId;
    if (!deviceId || !sourceKey) {
      setRows({});
      return;
    }

    let cancelled = false;

    const load = async () => {
      const found: RowsBySource = {};

      await Promise.all(
        sourceKey.split(',').map(async (id) => {
          const dataSourceId = Number(id);
          const { data } = await client.query({
            query: DataItemCellSelectGetDocument,
            variables: {
              dataSourceId,
              networkId: currentNetwork.id,
              page: 1,
              perPage: 25,
              search: deviceId,
            },
          });

          const source = data.network?.dataSource;
          const deviceKey = source?.deviceKey;
          if (!deviceKey) return;

          // `search` only narrows the fetch; the match itself has to be exact.
          const match = source.items.nodes.find(
            (node) => (node.data as Row)[deviceKey] === deviceId,
          );
          if (match) found[dataSourceId] = match.data as Row;
        }),
      );

      if (!cancelled) setRows(found);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [client, currentNetwork.id, settledDeviceId, sourceKey]);

  return useMemo(() => {
    const active = Boolean(previewDeviceId.trim());

    /**
     * The previewed value for an item, or undefined. Returned separately from the item on
     * purpose: it is display only, and must never reach anything that writes to the design.
     */
    const textFor = (item: StudioItem): string | undefined => {
      if (!active || item.type !== 'text') return undefined;
      if (!item.__deviceDataSourceId || !item.__dataItemKey) return undefined;

      return rows[item.__deviceDataSourceId]?.[item.__dataItemKey];
    };

    return { active, textFor };
  }, [previewDeviceId, rows]);
};
